import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../zodios/client";
import { toast, type Id } from "react-toastify";
import { AD_STATES } from "../../../components/core/blocks/AdPlan/constants";
import { isCampaignStateError } from "./campaignStateErrors";

export function useBulkUpdateAdState() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["advertising"]);
  return useMutation({
    mutationFn: async (vars: { ads: { id: number; name: string }[]; state: "active" | "paused" }) => {
      const stateId = vars.state === "active" ? AD_STATES.ACTIVE.value : AD_STATES.PAUSE.value;
      let toastId: Id | null = null;
      for (let i = 0; i < vars.ads.length; i++) {
        const ad = vars.ads[i]!;
        const progress = (i + 1) / vars.ads.length;
        try {
          await apiClient.setAdState({
            id: ad.id,
            state: stateId as 0 | 1,
          });
        } catch (e) {
          if (isCampaignStateError(e, "composite_ad")) {
            // Skip composite ads
          } else {
            toast(
              t(($) => $.advertising["Failed to update Media Plan {name}"], { name: ad.name }),
              { type: "error" },
            );
          }
        } finally {
          if (!toastId) {
            toastId = toast(
              t(($) => $.advertising["Updating Media Plans"], { count: vars.ads.length }),
              {
                progress,
                type: "info",
              },
            );
          } else {
            toast.update(toastId, { progress });
          }
        }
      }
      if (toastId) {
        toast.update(toastId, {
          render: t(($) => $.advertising["Media Plans Updated"], { count: vars.ads.length }),
          type: "success",
          progress: undefined,
        });
      }
    },
    onSuccess: () => {
      // TODO: shouldn't invalidate _all_ queries
      void queryClient.invalidateQueries();
    },
  });
}
